/**
 * usePlaybookLive
 * Fetches live market data and maps it to playbook option objects.
 * Returns { loading, error, liveData, selections } where selections is a 9-element array:
 *   [global(0), fii(1), expiry(2), timeSlot(3), gap(4), openBehavior(5), setup(6), vix(7), instrument(8)=null]
 *
 * Nodes 0–7 are auto-filled from live data. Instrument (8) is always null — user picks.
 *
 * When selectedSymbol is provided (e.g. 'RELIANCE.NS'):
 *   - Nodes 0,1,2,3,7 stay market-level (Nifty/global) — unchanged
 *   - Nodes 4,5,6 flip to stock-level (stock's own gap, open behavior, setup)
 *   - Node 8 auto-fills with an equity instrument object for the selected stock
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  GLOBAL_CONTEXTS, FII_CONTEXTS, EXPIRY_CONTEXTS, TIME_SLOTS,
  NIFTY_GAPS, OPEN_BEHAVIORS, INSTRUMENTS, VIX_OPTIONS, SETUPS, classifyVix, resolveSetup,
} from './playbookLogic';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function _mapMoodToGlobal(mood) {
  if (!mood) return GLOBAL_CONTEXTS[1]; // mixed
  const c = (mood.color || '').toLowerCase();
  if (c === '#16c784') return GLOBAL_CONTEXTS[0]; // green
  if (c === '#ef4444') return GLOBAL_CONTEXTS[2]; // red
  // cautiously bullish/bearish
  const label = (mood.label || '').toLowerCase();
  if (label.includes('bull')) return GLOBAL_CONTEXTS[0];
  if (label.includes('bear')) return GLOBAL_CONTEXTS[2];
  return GLOBAL_CONTEXTS[1]; // mixed/neutral
}

function _mapFiiNet(net) {
  if (net == null) return FII_CONTEXTS[1]; // neutral
  if (net > 0)    return FII_CONTEXTS[0]; // buying
  if (net < 0)    return FII_CONTEXTS[2]; // selling
  return FII_CONTEXTS[1];
}

function _mapExpiry(simDate) {
  // Use sim_date to determine expiry — not real today
  // Post-SEBI Sep-2025: all NSE contracts (Nifty, BankNifty) expire on Tuesday
  const day = simDate ? new Date(simDate + 'T00:00:00').getDay() : new Date().getDay();
  return day === 2 ? EXPIRY_CONTEXTS[1] : EXPIRY_CONTEXTS[0]; // 2 = Tuesday
}

function _mapTimeSlot() {
  // Convert local time to IST (UTC+5:30)
  const now = new Date();
  const istOffset = 5 * 60 + 30;
  const utcMins = now.getUTCHours() * 60 + now.getUTCMinutes();
  const mins = (utcMins + istOffset) % (24 * 60);

  // Market hours only — outside hours return null so user picks their intended slot
  if (mins >= 555 && mins < 585) return TIME_SLOTS[0]; // 9:15–9:45 opening
  if (mins >= 585 && mins < 690) return TIME_SLOTS[1]; // 9:45–11:30 mid_morning
  if (mins >= 690 && mins < 840) return TIME_SLOTS[2]; // 11:30–14:00 lunch_chop
  if (mins >= 840 && mins < 930) return TIME_SLOTS[3]; // 14:00–15:30 power_hour
  return null; // pre-market or post-market — user picks intended trading time
}

function _mapGapType(gapType) {
  return NIFTY_GAPS.find(g => g.id === gapType) || NIFTY_GAPS[2]; // flat fallback
}

function _mapOpenBehavior(regime) {
  if (!regime) return null;
  const ob = regime.open_behavior;
  if (ob) return OPEN_BEHAVIORS.find(o => o.id === ob) || OPEN_BEHAVIORS.find(o => o.id === 'mixed');
  // fallback for old regime rows that don't have open_behavior stored
  if (regime.conflict)                   return OPEN_BEHAVIORS.find(o => o.id === 'mixed');
  if (regime.match)                      return OPEN_BEHAVIORS.find(o => o.id === 'sustained');
  if (regime.gap_type === 'flat')        return OPEN_BEHAVIORS.find(o => o.id === 'range');
  return OPEN_BEHAVIORS.find(o => o.id === 'gap_fill');
}

// setup_type: prefer the backend-computed field (daily_regime.setup_type / stock-context
// API) — single source of truth, market_analysis_service.py. Only fall back to the
// client-side resolveSetup() re-derivation for stale payloads that predate that column.
function _mapSetupType(setupType, openOpt, gapOpt, timeSlotOpt) {
  if (setupType) return SETUPS.find(s => s.id === setupType) || resolveSetup(openOpt, gapOpt, timeSlotOpt);
  return resolveSetup(openOpt, gapOpt, timeSlotOpt);
}

export function usePlaybookLive(selectedSymbol = null) {
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [liveData, setLiveData]     = useState(null);
  const [selections, setSelections] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch global cues (market-level — always needed)
      const cuesRes = await axios.get(`${API_URL}/global-cues`);
      const cues = cuesRes.data;

      // Market-level nodes (0,1,2,3,7) — same regardless of stock selection
      const globalOpt   = _mapMoodToGlobal(cues.mood);
      const fiiNet      = cues.fii_dii?.fii?.net ?? null;
      const fiiOpt      = _mapFiiNet(fiiNet);
      const expiryOpt   = _mapExpiry(cues.sim_date); // use sim_date not real today
      const timeSlotOpt = _mapTimeSlot();
      const vixIndex    = (cues.indices || []).find(i => i.symbol === '^INDIAVIX');
      const vix         = vixIndex?.close || null;
      const vixLevel    = vix ? classifyVix(vix) : null;
      const vixOpt      = vixLevel ? VIX_OPTIONS.find(v => v.id === vixLevel.label.toLowerCase()) ?? null : null;
      const simDate     = cues.sim_date;
      const regime      = cues.regime || null;

      let gapOpt, openOpt, setupOpt, stockCtx = null, instrumentOpt = null;

      if (selectedSymbol) {
        // Stock-level nodes (4,5,6,8) — fetch stock context
        try {
          const stockRes = await axios.get(`${API_URL}/playbook/stock-context/${selectedSymbol}`);
          stockCtx = stockRes.data;
        } catch (_) {
          stockCtx = null;
        }

        if (stockCtx?.data_available) {
          gapOpt  = _mapGapType(stockCtx.gap_type);
          openOpt = OPEN_BEHAVIORS.find(o => o.id === stockCtx.open_behavior) || null;
          setupOpt = _mapSetupType(stockCtx.setup_type, openOpt, gapOpt, timeSlotOpt);
          // Node 8: auto-fill with stock equity instrument
          const ticker = selectedSymbol.replace('.NS', '');
          instrumentOpt = {
            ...INSTRUMENTS.find(i => i.id === 'equity'),
            label: `${ticker} — Equity`,
            sub:   'Stock selected — confirm instrument type',
          };
        } else {
          // Stock data unavailable — fall back to Nifty-level for 4,5,6
          gapOpt   = regime?.gap_type ? _mapGapType(regime.gap_type) : NIFTY_GAPS[2];
          openOpt  = _mapOpenBehavior(regime);
          setupOpt = _mapSetupType(regime?.setup_type, openOpt, gapOpt, timeSlotOpt);
        }
      } else {
        // Nifty mode — nodes 4,5,6 from market regime
        gapOpt   = regime?.gap_type ? _mapGapType(regime.gap_type) : NIFTY_GAPS[2];
        openOpt  = _mapOpenBehavior(regime);
        setupOpt = _mapSetupType(regime?.setup_type, openOpt, gapOpt, timeSlotOpt);
      }

      const sel = [
        globalOpt,     // 0: global — market level
        fiiOpt,        // 1: fii — market level
        expiryOpt,     // 2: expiry — market level
        timeSlotOpt,   // 3: timeSlot — runtime
        gapOpt,        // 4: gap — stock level if stock selected, else Nifty
        openOpt,       // 5: openBehavior — stock level if stock selected, else Nifty
        setupOpt,      // 6: setup — derived from 4+5
        vixOpt,        // 7: vix — market level
        instrumentOpt, // 8: auto-filled if stock selected, else null (user picks)
      ];

      setLiveData({ cues, regime, vix, simDate, stockCtx, selectedSymbol });
      setSelections(sel);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load live market data.');
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, liveData, selections, load };
}
